import $ from '@rover/globals/jquery';
import _ from '@rover/globals/underscore';
import Backbone from '@rover/globals/backbone';
import dataLayer from '@rover/globals/dataLayer';
import Rover from '@rover/globals/Rover';
import { GOOGLE_MAP_CUSTOM_STYLE } from './constants';
import SearchResultMarkerCollection from '../../collections/search/search_result_marker_collection';
import SearchResultMarkerCollectionView from './search_result_marker_collection_view';

export default Backbone.View.extend({
  // A view for the map on the right side.  It has references to both the
  // Query and SearchResults objects.  It is responsible for setting up
  // the collection of markers, listening for map changes coming from SearchResults,
  // as well as triggering searches.

  initialize(options) {
    this.query = options.query;
    this.searchResults = options.searchResults;
    this.mapSearchData = options.mapSearchData;
    this.mapConfig = options.mapConfig;
    this.oldMapCenter = null;
    this.$mapDiv = this.$('.js-search-map');
    this.boundsInitialized = false;
    this.highlightedCallouts = {};
  },

  render() {
    // Don't handle the map idle event caused by
    // this view updating the map. We set it to
    // true to ignore the initial idle event
    this.updatingMap = true;

    if (!Object.prototype.hasOwnProperty.call(window, 'google')) {
      return;
    }

    // Show loading screen initially
    this.setMapHeight();
    this.$mapDiv.addClass('rover-loading');
    google.maps.event.addDomListener(window, 'load', () => {
      this.$mapDiv.removeClass('rover-loading');
    });

    this.map = new google.maps.Map(this.$mapDiv[0], this.getMapOptions());

    this.map.topZIndex = google.maps.Marker.MAX_ZINDEX + 100;

    // set up handler on map
    google.maps.event.addListener(this.map, 'idle', () => {
      this.onMapIdle();
    });

    google.maps.event.addListener(this.map, 'dragend', () => {
      this.onMapDragEnd();
    });

    google.maps.event.addListener(this.map, 'zoom_changed', () => {
      this.onMapZoomChanged();
    });

    // Update the map when search results change
    this.searchResults.on('change', _.bind(this.onSearchResultsChanged, this));

    // Trigger map resize event on mapSize change
    $(window).resize(_.bind(this.setMapHeight, this));
    Rover.pubSub.on('map:resize', _.bind(this.onMapResize, this));

    // Listen for map updates
    Rover.pubSub.on('map:updating', _.bind(this.onMapUpdating, this));

    // Collections and views of markers
    _.each(Rover.serviceTypesJson, serviceType => {
      const collection = new SearchResultMarkerCollection({
        searchResults: this.searchResults,
        service_type: serviceType.slug,
      });
      const view = new SearchResultMarkerCollectionView({
        searchResults: this.searchResults,
        collection,
        map: this.map,
      });
      view.render();
    });

    // Initial update
    this.onSearchResultsChanged();
    this.setMapHeight();

    // hide any map markers that should be concealed
    // if the map moves this is picked up by marker onSearchResultsChanged
    Rover.pubSub.trigger('search:hide-concealed-markers');
  },

  highlightCalloutsAreas() {
    this.clearPolygons();

    // Callouts should already always be an array but just to be safe...
    const callouts = this.searchResults.safeGet('callouts', []);
    if (!callouts.length) {
      return;
    }

    // Each callout (Seattle is the only one currently)
    // has a data field with a polygon. Because it is a multiPolygon it is a
    // list of lists of coordinates [[[lng, lat], [lng, lat], ...], ...]
    // Render only the exterior ring
    callouts.forEach(callout => {
      if (!callout.data.location || !callout.data.polygon) {
        return;
      }
      if (!this.highlightedCallouts[callout.data.location]) {
        this.highlightedCallouts[callout.data.location] = [];
        const multiPolygon = callout.data.polygon;

        multiPolygon.forEach(polygon => {
          const exteriorRing = polygon[0];
          this.highlightedCallouts[callout.data.location].push(this.highlightPolygon(exteriorRing));
        });

        if (callout.slug == 'dilla-area-overlap') {
          dataLayer.push({
            event: 'search-on-demand-callout-show',
            zipCode: this.query.attributes.location,
            serviceType: this.query.attributes.service_type,
          });
        }
      }
    });
  },

  highlightPolygon(polygon) {
    const coords = polygon.map(lngLatPair => ({
      lat: lngLatPair[1],
      lng: lngLatPair[0],
    }));

    // Construct the polygon.
    const dillaSearchArea = new google.maps.Polygon({
      paths: coords,
      strokeColor: '#777777',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#777777',
      fillOpacity: 0.35,
    });
    dillaSearchArea.setMap(this.map);
    return dillaSearchArea;
  },

  clearPolygons() {
    Object.keys(this.highlightedCallouts).forEach(key => {
      const polygons = this.highlightedCallouts[key];
      polygons.forEach(polygon => {
        polygon.setMap(null);
      });
    });
    this.highlightedCallouts = {};
  },

  getMapOptions() {
    const options = {
      scrollwheel: false,
      center: new google.maps.LatLng('47.6097', '-122.3331'),
      zoom: 8,
      minZoom: 8,
      maxZoom: 16,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      fullscreenControl: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      styles: GOOGLE_MAP_CUSTOM_STYLE,
    };

    if (this.query.get('centerlat') && this.query.get('centerlng')) {
      options.center = new google.maps.LatLng(
        this.query.get('centerlat'),
        this.query.get('centerlng')
      );
    }
    return options;
  },

  setMapHeight() {
    // Resize the map and set the center
    const height = $(window).height();
    this.$mapDiv.height(height);

    // Set the oldMapCenter if it exists
    if (this.oldMapCenter) {
      google.maps.event.trigger(this.map, 'resize');
      this.map.setCenter(this.oldMapCenter);
    }
  },

  onMapResize() {
    // We don't set this.updatingMap to true since we want people
    // to be able to click the button to update the search results
    // in the map.
    google.maps.event.trigger(this.map, 'resize');

    // Set the oldMapCenter if it exists
    if (this.oldMapCenter) {
      this.map.setCenter(this.oldMapCenter);
    }

    this.onMapIdle();
  },

  onMapUpdating() {
    this.updatingMap = true;
  },

  onMapIdle() {
    // Set the oldMapCenter
    google.maps.event.trigger(this.map, 'resize');
    this.oldMapCenter = this.map.getCenter();

    // Don't set new parameters if we are updating the map
    if (this.updatingMap) {
      this.updatingMap = false;
      return;
    }

    if (!this.$mapDiv.is(':visible') || this.$mapDiv.css('visibility') === 'hidden') {
      return;
    }

    // Get the map bounds
    const bounds = this.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const center = this.map.getCenter();

    // Set the bounds on this model
    this.mapSearchData.set({
      zoomlevel: this.map.getZoom(),
      centerlat: center.lat(),
      centerlng: center.lng(),
      minlat: sw.lat(),
      minlng: sw.lng(),
      maxlat: ne.lat(),
      maxlng: ne.lng(),
    });
  },

  onMapDragEnd() {
    dataLayer.push({ event: 'experiment-search-map-moved' });
  },

  onMapZoomChanged() {
    // zoom-changed event is always called when the map loads so set the
    // current zoom level
    if (this.mapSearchData.get('zoomlevel') == null) {
      this.mapSearchData.set('zoomlevel', this.map.getZoom());
    } else {
      dataLayer.push({ event: 'experiment-search-map-zoomed' });

      if (this.mapSearchData.get('zoomlevel') > this.map.getZoom()) {
        dataLayer.push({ event: 'experiment-search-map-zoomed-out' });
      } else {
        dataLayer.push({ event: 'experiment-search-map-zoomed-in' });
      }
    }
  },

  onSearchResultsChanged() {
    const derivedData = this.searchResults.get('derived_data');
    const searchedQuery = this.searchResults.get('query');
    const alternateType = this.searchResults.get('alternate_type');
    let validBounds = false;

    if (!derivedData) {
      return;
    }

    this.highlightCalloutsAreas();

    // Show the map
    google.maps.event.trigger(this.map, 'resize');

    // Don't update the map's location if we did a map-based search
    if (this.mapSearchData.boundsSameAs(derivedData)) {
      return;
    }

    // Fit all of the result markers
    const bounds = new google.maps.LatLngBounds();
    const results = this.searchResults.getAllResults();
    const viewport = this.searchResults.get('viewport');
    if (viewport) {
      bounds.extend(new google.maps.LatLng(viewport.minlat, viewport.minlng));
      bounds.extend(new google.maps.LatLng(viewport.maxlat, viewport.maxlng));
      validBounds = true;
    } else {
      _.each(results, result => {
        bounds.extend(new google.maps.LatLng(result.latitude, result.longitude));
      });
      if (results.length) {
        validBounds = true;
      }
    }

    // Mark map as being updated
    this.updatingMap = true;

    if (derivedData.zoom_method === 'dass' || !this.boundsInitialized) {
      if (validBounds) {
        this.fitBounds(bounds);
      } else {
        // We just set the center of the map when we don't have
        // any results
        this.map.setCenter(
          new google.maps.LatLng(
            derivedData.center[0], // latitude
            derivedData.center[1]
          )
        ); // longitude
        this.map.setZoom(12);
      }
      this.boundsInitialized = true;
    }
  },

  fitBounds(bounds) {
    this.map.setCenter(bounds.getCenter());

    const mapDim = {
      height: this.$mapDiv.height(),
      width: this.$mapDiv.width(),
    };

    if (mapDim.height === 0) {
      _.delay(_.bind(this.fitBounds, this), 100, bounds);
      return;
    }

    const zoomlevel = this.getBoundsZoomLevel(bounds, mapDim);
    this.map.setZoom(zoomlevel);
  },

  getBoundsZoomLevel(bounds, mapDim) {
    const WORLD_DIM = { height: 256, width: 256 };
    const ZOOM_MAX = 21;

    function latRad(lat) {
      const sin = Math.sin(lat * Math.PI / 180);
      const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
      return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx, worldPx, fraction) {
      return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  },
});



// WEBPACK FOOTER //
// ./js/backbone/views/search/search_map_view.js